import React from 'react';
import { Box, Portal, Tooltip } from '@mui/material';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { useDrag, useDragDropManager, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { MRT_TableInstance } from 'material-react-table';
import { ProjectLineItemDTO, ProjectLineItemSubtotalDTO, SubtotalReanchorDTO } from './project.type';

// Drag-to-reorder for draw line item grids (DrawLineItemsTable.tsx, ProjectTable.tsx). The whole table container is a
// single drop target and the drop point is resolved from the cursor, so dropping in the gap between rows works.
//
// Rows move in units: a free line item on its own, or a subtotal together with the rows it counts. Rows inside a
// subtotal aren't reordered here — that's done by editing the subtotal.

export const LINE_ITEM_ROW_REORDER_TYPE = 'LINE_ITEM_ROW_REORDER';

const ROW_ID_ATTR = 'data-reorder-row-id';

// `scope` (the draw id) keeps a drag from one table from dropping into another table on the same page.
export type RowReorderDragItem = { scope: string; rowIds: string[] };
export type RowReorderTarget = { unitId: string; position: 'before' | 'after' };
// A subtotal that would otherwise count different rows after the move gets its range pinned to its current row count.
export type SubtotalRangeChange = { subtotalId: string; range: number };
export type RowReorderResult = { lineItems: ProjectLineItemDTO[]; rangeChanges: SubtotalRangeChange[] };

// Unit id is the line item's own id for a free row, or the subtotal id for a subtotal and its rows.
type ReorderUnit = { id: string; rowIds: string[] };

// Scrolls `rowId` into view once it renders, switching to its page first in a paginated table. Gives up if a filter
// hides the row.
export function useScrollToRow(
  table: MRT_TableInstance<ProjectLineItemDTO>,
  containerRef: React.RefObject<HTMLElement | null>,
  rowId: string | null,
  onDone: () => void,
) {
  React.useEffect(() => {
    if (!rowId || !table.getCoreRowModel().rowsById[rowId]) return;

    const element = containerRef.current?.querySelector<HTMLElement>(`[${ROW_ID_ATTR}="${CSS.escape(rowId)}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      onDone();
      return;
    }

    const index = table.getPrePaginationRowModel().rows.findIndex(row => row.id === rowId);
    const { pageIndex, pageSize } = table.getState().pagination;
    const targetPage = Math.floor(index / pageSize);
    if (index < 0 || !table.options.enablePagination || targetPage === pageIndex) onDone();
    else table.setPageIndex(targetPage);
  });
}

// Spread into muiTableBodyRowProps so the drop target can locate rows.
export const getReorderRowAttributes = (row: ProjectLineItemDTO): Record<`data-${string}`, string> => ({ [ROW_ID_ATTR]: row.id });

// Active line items in display order — computeSubtotalDisplayData only sorts by serialNumber when subtotals exist.
const getDisplayOrder = (lineItems: ProjectLineItemDTO[], subtotals: ProjectLineItemSubtotalDTO[]) => {
  const active = lineItems.filter(li => !li.isDeleted);
  return subtotals.length ? [...active].sort((a, b) => (a.serialNumber ?? 0) - (b.serialNumber ?? 0)) : active;
};

// Rows each subtotal counts, using the same ranges as computeSubtotalDisplayData.
const getSubtotalRows = (ordered: ProjectLineItemDTO[], subtotals: ProjectLineItemSubtotalDTO[]) => {
  const indexById = new Map(ordered.map((li, index) => [li.id, index]));
  const anchorIndex = (s: ProjectLineItemSubtotalDTO) => indexById.get(s.anchorDrawLineItemId) ?? -1;
  const anchored = subtotals.filter(s => anchorIndex(s) >= 0).sort((a, b) => anchorIndex(a) - anchorIndex(b));

  const rowsBySubtotalId = new Map<string, string[]>();
  let start = 0;
  anchored.forEach(s => {
    const end = anchorIndex(s) + 1;
    const from = s.range != null && s.range < end - start ? end - s.range : start;
    rowsBySubtotalId.set(
      s.id,
      ordered.slice(from, end).map(li => li.id),
    );
    start = end;
  });
  return rowsBySubtotalId;
};

const getUnits = (ordered: ProjectLineItemDTO[], subtotals: ProjectLineItemSubtotalDTO[]) => {
  const rowsBySubtotalId = getSubtotalRows(ordered, subtotals);
  const unitIdByRowId = new Map<string, string>();
  rowsBySubtotalId.forEach((rowIds, subtotalId) => rowIds.forEach(id => unitIdByRowId.set(id, subtotalId)));

  const units: ReorderUnit[] = [];
  ordered.forEach(li => {
    const unitId = unitIdByRowId.get(li.id) ?? li.id;
    unitIdByRowId.set(li.id, unitId);
    const last = units[units.length - 1];
    if (last?.id === unitId) last.rowIds.push(li.id);
    else units.push({ id: unitId, rowIds: [li.id] });
  });

  // A subtotal row renders right after its anchor, so one that counts no rows belongs to its anchor's unit.
  subtotals.forEach(s => {
    if (!rowsBySubtotalId.get(s.id)?.length) unitIdByRowId.set(s.id, unitIdByRowId.get(s.anchorDrawLineItemId) ?? s.id);
    else unitIdByRowId.set(s.id, s.id);
  });

  return { units, unitIdByRowId, rowsBySubtotalId };
};

// Moves the units containing `rowIds` before/after the target unit and renumbers serialNumber from 1 across active items
// (deleted items keep their slots). Returns null when the order doesn't change.
export const reorderLineItems = (
  lineItems: ProjectLineItemDTO[],
  allSubtotals: ProjectLineItemSubtotalDTO[],
  rowIds: string[],
  target: RowReorderTarget,
): RowReorderResult | null => {
  const subtotals = allSubtotals.filter(s => !s.isDeleted);
  const ordered = getDisplayOrder(lineItems, subtotals);
  const { units, unitIdByRowId, rowsBySubtotalId } = getUnits(ordered, subtotals);

  const moving = new Set(rowIds.map(id => unitIdByRowId.get(id) ?? id));
  const rest = units.filter(u => !moving.has(u.id));
  const targetIndex = rest.findIndex(u => u.id === target.unitId);
  if (rest.length === units.length || targetIndex < 0) return null;

  const insertAt = target.position === 'before' ? targetIndex : targetIndex + 1;
  const nextIds = [...rest.slice(0, insertAt), ...units.filter(u => moving.has(u.id)), ...rest.slice(insertAt)].flatMap(u => u.rowIds);
  if (nextIds.every((id, i) => id === ordered[i].id)) return null;

  const byId = new Map(ordered.map(li => [li.id, li]));
  const renumbered = nextIds.map((id, index) => {
    const li = byId.get(id) as ProjectLineItemDTO;
    return li.serialNumber === index + 1 ? li : { ...li, serialNumber: index + 1, isEdited: li.isCreated ? li.isEdited : true };
  });

  // Units stay contiguous and end at their anchor, so pinning the range to the old count restores the same rows.
  const nextRowsBySubtotalId = getSubtotalRows(renumbered, subtotals);
  const rangeChanges = subtotals.flatMap(s => {
    const before = rowsBySubtotalId.get(s.id);
    const after = nextRowsBySubtotalId.get(s.id);
    return before && after && before.join() !== after.join() ? [{ subtotalId: s.id, range: before.length }] : [];
  });

  let i = 0;
  return { lineItems: lineItems.map(li => (li.isDeleted ? li : renumbered[i++])), rangeChanges };
};

// Applies pinned ranges to the draw's subtotals. Persisted ones are flushed on Save via pendingSubtotalReanchors, same as
// range edits; temp-subtotal- ones are read fresh from `subtotals` at save time.
export const applySubtotalRangeChanges = (
  subtotals: ProjectLineItemSubtotalDTO[] | undefined,
  pendingReanchors: SubtotalReanchorDTO[] | undefined,
  rangeChanges: SubtotalRangeChange[],
) => {
  if (!rangeChanges.length || !subtotals) return { subtotals, pendingSubtotalReanchors: pendingReanchors };

  const rangeById = new Map(rangeChanges.map(c => [c.subtotalId, c.range]));
  const nextSubtotals = subtotals.map(s => (rangeById.has(s.id) ? { ...s, range: rangeById.get(s.id) } : s));
  const reanchors = nextSubtotals
    .filter(s => rangeById.has(s.id) && !s.id.startsWith('temp-subtotal-'))
    .map(s => ({ subtotalId: s.id, newAnchorDrawLineItemId: s.anchorDrawLineItemId, range: s.range }));

  return {
    subtotals: nextSubtotals,
    pendingSubtotalReanchors: [...(pendingReanchors ?? []).filter(r => !rangeById.has(r.subtotalId)), ...reanchors],
  };
};

type DropPoint = { target: RowReorderTarget; lineTop: number; left: number; width: number };

const isSameDropPoint = (a: DropPoint | null, b: DropPoint | null) =>
  a === b ||
  (!!a &&
    !!b &&
    a.target.unitId === b.target.unitId &&
    a.target.position === b.target.position &&
    a.lineTop === b.lineTop &&
    a.left === b.left &&
    a.width === b.width);

// Finds the gap between units nearest the cursor (snapping to a unit's top or bottom edge when the cursor is inside it)
// and targets the first unit below it that isn't being moved, or the last one above it at the end of the list.
const resolveDropPoint = (
  container: HTMLElement,
  clientY: number,
  unitIdByRowId: Map<string, string>,
  movingUnitIds: Set<string>,
): DropPoint | null => {
  const rows = Array.from(container.querySelectorAll<HTMLElement>(`[${ROW_ID_ATTR}]`));
  if (!rows.length) return null;

  const unitOf = (index: number) => {
    const id = rows[index].getAttribute(ROW_ID_ATTR) ?? '';
    return unitIdByRowId.get(id) ?? id;
  };

  let gap = 0;
  let hi = rows.length;
  while (gap < hi) {
    const mid = (gap + hi) >> 1;
    const rect = rows[mid].getBoundingClientRect();
    if (clientY < rect.top + rect.height / 2) hi = mid;
    else gap = mid + 1;
  }

  if (gap > 0 && gap < rows.length && unitOf(gap - 1) === unitOf(gap)) {
    const unitId = unitOf(gap);
    let first = gap - 1;
    while (first > 0 && unitOf(first - 1) === unitId) first--;
    let last = gap;
    while (last < rows.length - 1 && unitOf(last + 1) === unitId) last++;
    const top = rows[first].getBoundingClientRect().top;
    const bottom = rows[last].getBoundingClientRect().bottom;
    gap = clientY < (top + bottom) / 2 ? first : last + 1;
  }

  let targetIndex = gap;
  while (targetIndex < rows.length && movingUnitIds.has(unitOf(targetIndex))) targetIndex++;
  const isBefore = targetIndex < rows.length;
  if (!isBefore) {
    targetIndex = gap - 1;
    while (targetIndex >= 0 && movingUnitIds.has(unitOf(targetIndex))) targetIndex--;
    if (targetIndex < 0) return null;
  }

  const containerRect = container.getBoundingClientRect();
  const y = isBefore ? rows[targetIndex].getBoundingClientRect().top : rows[rows.length - 1].getBoundingClientRect().bottom;

  return {
    target: { unitId: unitOf(targetIndex), position: isBefore ? 'before' : 'after' },
    lineTop: Math.min(Math.max(y, containerRect.top), containerRect.bottom),
    left: containerRect.left,
    width: containerRect.width,
  };
};

export function RowDragHandle({ scope, rowIds, disabledReason }: { scope: string; rowIds: string[] | null; disabledReason?: string }) {
  const canDrag = !!rowIds?.length;

  const [{ isDragging }, dragRef, preview] = useDrag<RowReorderDragItem, void, { isDragging: boolean }>({
    type: LINE_ITEM_ROW_REORDER_TYPE,
    item: { scope, rowIds: rowIds ?? [] },
    canDrag,
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  });

  // CustomDragLayer renders the "N rows" preview instead.
  React.useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <Tooltip title={isDragging ? '' : canDrag ? 'Drag to reorder' : (disabledReason ?? '')} placement='top'>
      <Box
        ref={dragRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: canDrag ? 'grab' : 'default',
          color: canDrag ? 'text.secondary' : 'action.disabled',
          opacity: isDragging ? 0.5 : 1,
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: '1.1rem' }} />
      </Box>
    </Tooltip>
  );
}

RowDragHandle.displayName = 'RowDragHandle';

// Registers `containerRef` as the drop target, auto-scrolls the window/container near their edges while dragging, and
// returns the insertion line to render anywhere in the table component plus what each row's grip drags.
export function useRowReorderDrop({
  scope,
  containerRef,
  lineItems,
  subtotals,
  rowSelection,
  disabled,
  onReorder,
}: {
  scope: string;
  containerRef: React.RefObject<HTMLElement | null>;
  lineItems: ProjectLineItemDTO[];
  subtotals: ProjectLineItemSubtotalDTO[];
  rowSelection?: Record<string, boolean>;
  disabled?: boolean;
  onReorder: (result: RowReorderResult) => void;
}) {
  const [dropPoint, setDropPoint] = React.useState<DropPoint | null>(null);
  // Only the table the drag is over auto-scrolls, so several mounted tables don't scroll the window together.
  const isActiveRef = React.useRef(false);

  const { units, unitIdByRowId } = React.useMemo(() => {
    const activeSubtotals = subtotals.filter(s => !s.isDeleted);
    return getUnits(getDisplayOrder(lineItems, activeSubtotals), activeSubtotals);
  }, [lineItems, subtotals]);

  const selectedIds = React.useMemo(
    () => Object.keys(rowSelection ?? {}).filter(id => rowSelection?.[id] && unitIdByRowId.has(id)),
    [rowSelection, unitIdByRowId],
  );

  // Selected rows move together only when they're free rows sitting next to each other.
  const selectedBlock = React.useMemo(() => {
    if (selectedIds.length < 2) return null;
    const unitIndexById = new Map(units.map((u, index) => [u.id, index]));
    const indexes = selectedIds.map(id => unitIndexById.get(id) ?? -1).sort((a, b) => a - b);
    const isContiguous = indexes[0] >= 0 && indexes[indexes.length - 1] - indexes[0] === indexes.length - 1;
    return isContiguous ? indexes.map(index => units[index].id) : null;
  }, [selectedIds, units]);

  // Grip on a free row drags that row (or the whole selection), a subtotal's grip drags the subtotal with its rows, rows
  // inside a subtotal can't be dragged.
  const getRowDrag = React.useCallback(
    (rowId: string): { rowIds: string[] | null; disabledReason?: string } => {
      const unitId = unitIdByRowId.get(rowId) ?? rowId;
      const isSubtotalRow = subtotals.some(s => s.id === rowId);
      if (unitId !== rowId && !isSubtotalRow) return { rowIds: null, disabledReason: 'Edit the subtotal to reorder its rows' };
      if (selectedIds.length > 1 && selectedIds.includes(rowId)) {
        return selectedBlock
          ? { rowIds: selectedBlock }
          : { rowIds: null, disabledReason: 'Selected rows must be next to each other and outside subtotals to move together' };
      }
      return { rowIds: units.find(u => u.id === unitId)?.rowIds ?? null };
    },
    [units, unitIdByRowId, subtotals, selectedIds, selectedBlock],
  );

  const accepts = (item: RowReorderDragItem) => !disabled && item.scope === scope;

  const getDropPoint = (item: RowReorderDragItem, clientY: number | undefined) => {
    const container = containerRef.current;
    if (clientY == null || !container) return null;
    const movingUnitIds = new Set(item.rowIds.map(id => unitIdByRowId.get(id) ?? id));
    return resolveDropPoint(container, clientY, unitIdByRowId, movingUnitIds);
  };

  const [{ isOver, draggedRowIds }, dropRef] = useDrop<RowReorderDragItem, void, { isOver: boolean; draggedRowIds?: string[] }>({
    accept: LINE_ITEM_ROW_REORDER_TYPE,
    canDrop: accepts,
    hover: (item, monitor) => {
      if (!accepts(item)) return;
      isActiveRef.current = true;
      const next = getDropPoint(item, monitor.getClientOffset()?.y);
      setDropPoint(prev => (isSameDropPoint(prev, next) ? prev : next));
    },
    drop: (item, monitor) => {
      const point = getDropPoint(item, monitor.getClientOffset()?.y);
      const result = point && reorderLineItems(lineItems, subtotals, item.rowIds, point.target);
      if (result) onReorder(result);
    },
    collect: monitor => ({
      isOver: monitor.isOver() && monitor.canDrop(),
      draggedRowIds: monitor.getItemType() === LINE_ITEM_ROW_REORDER_TYPE ? monitor.getItem()?.rowIds : undefined,
    }),
  });

  dropRef(containerRef);

  // For dimming every row being moved, including a dragged subtotal's own row.
  const draggedUnitIds = React.useMemo(
    () => new Set((draggedRowIds ?? []).map(id => unitIdByRowId.get(id) ?? id)),
    [draggedRowIds, unitIdByRowId],
  );
  const isRowDragged = React.useCallback(
    (rowId: string) => draggedUnitIds.has(unitIdByRowId.get(rowId) ?? rowId),
    [draggedUnitIds, unitIdByRowId],
  );

  React.useEffect(() => {
    if (!isOver) setDropPoint(null);
  }, [isOver]);

  const monitor = useDragDropManager().getMonitor();
  React.useEffect(() => {
    let animationFrameId: number | null = null;
    let isScrolling = false;
    const windowScrollThreshold = 120; // px from top/bottom of viewport
    const containerScrollThreshold = 90; // px from top/bottom of container
    const baseScrollStep = 8;
    const maxMultiplier = 4;

    const scrollLoop = () => {
      if (!monitor.isDragging() || monitor.getItemType() !== LINE_ITEM_ROW_REORDER_TYPE || !isActiveRef.current) {
        isScrolling = false;
        return;
      }

      const offset = monitor.getClientOffset();
      if (!offset) {
        isScrolling = false;
        return;
      }

      const { y } = offset;
      let shouldContinueScrolling = false;

      const container = containerRef.current;

      // Window scrolling near viewport edges
      const viewportHeight = window.innerHeight;
      const distanceFromTop = y;
      const distanceFromBottom = viewportHeight - y;
      if (distanceFromTop < windowScrollThreshold) {
        const canScrollWindowUp = window.scrollY > 0;
        if (canScrollWindowUp) {
          const proximity = 1 - distanceFromTop / windowScrollThreshold;
          const speedFactor = Math.min(maxMultiplier, 1 + proximity * 3);
          window.scrollBy({ top: -baseScrollStep * speedFactor, behavior: 'auto' });
          shouldContinueScrolling = true;
        }
      } else if (distanceFromBottom < windowScrollThreshold) {
        const canScrollWindowDown = window.scrollY + viewportHeight < document.documentElement.scrollHeight;
        if (canScrollWindowDown) {
          const proximity = 1 - distanceFromBottom / windowScrollThreshold;
          const speedFactor = Math.min(maxMultiplier, 1 + proximity * 3);
          window.scrollBy({ top: baseScrollStep * speedFactor, behavior: 'auto' });
          shouldContinueScrolling = true;
        }
      }

      // Container scrolling near container edges
      if (container) {
        const rect = container.getBoundingClientRect();
        const withinX = offset.x >= rect.left && offset.x <= rect.right;
        const withinYBand = y >= rect.top - containerScrollThreshold && y <= rect.bottom + containerScrollThreshold;
        if (withinX && withinYBand) {
          const distanceFromContainerTop = y - rect.top;
          const distanceFromContainerBottom = rect.bottom - y;
          if (distanceFromContainerTop < containerScrollThreshold && distanceFromContainerTop >= 0) {
            const canScrollUp = container.scrollTop > 0;
            if (canScrollUp) {
              const proximity = 1 - distanceFromContainerTop / containerScrollThreshold;
              const speedFactor = Math.min(maxMultiplier, 1 + proximity * 3);
              container.scrollBy({ top: -baseScrollStep * speedFactor, behavior: 'auto' });
              shouldContinueScrolling = true;
            }
          } else if (distanceFromContainerBottom < containerScrollThreshold && distanceFromContainerBottom >= 0) {
            const canScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight;
            if (canScrollDown) {
              const proximity = 1 - distanceFromContainerBottom / containerScrollThreshold;
              const speedFactor = Math.min(maxMultiplier, 1 + proximity * 3);
              container.scrollBy({ top: baseScrollStep * speedFactor, behavior: 'auto' });
              shouldContinueScrolling = true;
            }
          }
        }
      }

      if (shouldContinueScrolling) {
        isScrolling = true;
        animationFrameId = requestAnimationFrame(scrollLoop);
      } else {
        isScrolling = false;
      }
    };

    const handleOffsetChange = () => {
      if (!monitor.isDragging()) {
        isActiveRef.current = false;
        return;
      }
      if (!isScrolling) scrollLoop();
    };

    const unsubscribeOffset = monitor.subscribeToOffsetChange(handleOffsetChange);

    return () => {
      unsubscribeOffset();
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [monitor, containerRef]);

  const dropIndicator = dropPoint && (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          top: dropPoint.lineTop - 1,
          left: dropPoint.left,
          width: dropPoint.width,
          height: 2,
          bgcolor: 'primary.main',
          pointerEvents: 'none',
          zIndex: 'tooltip',
        }}
      />
    </Portal>
  );

  return { dropIndicator, getRowDrag, isRowDragged };
}
